// Role Selection Page - Updated to match the image design
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import { useAuth } from "../contexts/AuthContext";
import Button from "../components/Button";
import useSnackbarNotification from "../hooks/useSnackbarNotification";

const RoleSelectionPage = () => {
  const { createProfileInfo } = useProfileInfo();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showError } = useSnackbarNotification();
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleRoleSelection = async (role) => {
    setLoading(true);
    try {
      // Check if user has existing metadata (existing user)
      const hasExistingData = user?.user_metadata && Object.keys(user.user_metadata).length > 0;
      
      await createProfileInfo({ 
        role,
        onboardingStep: 0,
        onboardingCompleted: false,
        firstTimeLogin: !hasExistingData  // True only for truly new users
      });
      
      // Route based on role naming (Employer -> company setup page, Employee -> personal profile)
      if (role === 'Employer') {
        navigate("/employer-profile", { replace: true });
      } else {
        navigate("/employee-profile", { replace: true });
      }
    } catch (error) {
      console.error("Error creating profile:", error);
      showError("Failed to create profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleExistingUser = () => {
    // Navigate to the main dashboard/home page for existing users
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-10 px-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full">
        {/* Header with BETA tag */}
        <div className="flex items-center justify-center mb-6">
          <h1 className="text-3xl font-bold text-black mr-3">Doctors' Jobs</h1>
          <span className="bg-gray-200 text-black text-xs px-2 py-1 rounded">BETA</span>
        </div>

        {/* Welcome message */}
        <div className="text-left mb-8">
          <h2 className="text-2xl font-bold text-black mb-4">Welcome to Doctors' Jobs!</h2>
          <p className="text-gray-600 text-sm">
            Track opportunities, sharpen your resume, and take control of your next career move, with support that's designed just for doctors.
          </p>
        </div>

        {/* Role selection cards - only two cards */}
        <div className="flex gap-3 mb-8">
          {/* Get a Job Card */}
          <div 
            className={`flex-1 rounded-xl py-16 px-4 cursor-pointer transition-all duration-200 flex flex-col items-center justify-center gap-4 ${
              selectedRole === 'Employee' 
                ? 'bg-blue-100 border-2 border-blue-300' 
                : 'bg-white border border-gray-300 hover:border-gray-400'
            }`}
            onClick={() => setSelectedRole('Employee')}
          >
            <img 
              src="/Find a new role.svg" 
              alt="Find a new role" 
              className="w-10 h-10"
            />
            <h3 className="text-lg font-semibold text-black text-center">Find a new role</h3>
          </div>

          {/* Hire Talent Card */}
          <div 
            className={`flex-1 rounded-xl py-16 px-4 cursor-pointer transition-all duration-200 flex flex-col items-center justify-center gap-4 ${
              selectedRole === 'Employer' 
                ? 'bg-blue-100 border-2 border-blue-300' 
                : 'bg-white border border-gray-300 hover:border-gray-400'
            }`}
            onClick={() => setSelectedRole('Employer')}
          >
            <img 
              src="/Hire Talent.svg" 
              alt="Hire Talent" 
              className="w-10 h-10"
            />
            <h3 className="text-lg font-semibold text-black text-center">Hire Talent</h3>
          </div>
        </div>

        {/* Get started button - centered */}
        <div className="flex flex-col items-center gap-4">
          {selectedRole && (
            <Button
              onClick={() => handleRoleSelection(selectedRole)}
              disabled={loading}
              className="bg-black text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 transition-colors"
            >
              {loading ? "Creating Profile..." : "Get started"}
            </Button>
          )}
          {/* <button
            onClick={handleExistingUser}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Already have an account? <span className="underline">Log In</span>
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default RoleSelectionPage;
